import React, { ReactElement, RefCallback, RefObject } from 'react';
import camelcase from 'camelcase-es5';

import Icon from '../Icon/Icon';
import Box from '../Box/Box';
import Text from '../Text';

type TSelectionInput = {
  /** value of the checkbox dom input*/
  value: string;
  /** The id of the checkbox input, also used on the label as htmlFor */
  id?: string;
  /** Whether it is checked */
  isChecked: boolean;
  /** The label text. Also passed into aria-label attribute on the input itself*/
  labelText: string;
  ariaLabel?: string;
  // ** Count of items in a formatted string */
  counter?: string;
  /** Only visible on mobile + stacked */
  icon?: string;
  /** Only visible on mobile + stacked */
  helperText?: string;
  disabled?: boolean;
  /** On component change */
  onChange?: React.FormEventHandler<HTMLInputElement>;
  /** Component name */
  componentName: string;
  CheckMark?: () => ReactElement;
  inputRef?: RefObject<HTMLInputElement> | RefCallback<HTMLInputElement>;
  name?: string;
} & React.HTMLAttributes<any>;

type TSelectionInputBody = {
  icon?: string;
  labelText: string;
  counter?: string;
  helperText?: string;
  id: string;
};

type TInput = {
  ariaLabel?: string;
  helperText?: string;
  isChecked: boolean;
  id: string;
  onChange?: React.FormEventHandler<HTMLInputElement>;
  componentName: string;
  value: string;
  inputRef?: RefObject<HTMLInputElement> | RefCallback<HTMLInputElement>;
  disabled?: boolean;
  htmlNameAttr?: string;
} & React.InputHTMLAttributes<any>;

// eslint-disable-next-line react/display-name
const Input = ({
  value,
  isChecked,
  onChange,
  ariaLabel,
  id,
  componentName,
  inputRef,
  disabled,
  htmlNameAttr,
}: TInput) => (
  <Box
    parent="SelectionInput"
    name="Input"
    as="input"
    type={componentName}
    id={id}
    ref={inputRef}
    htmlNameAttr={htmlNameAttr ? htmlNameAttr : id}
    checked={isChecked}
    value={value}
    onChange={onChange}
    aria-label={ariaLabel}
    disabled={disabled}
  />
);

const SelectionInputBody = ({ id, labelText, counter, helperText, icon }: TSelectionInputBody): ReactElement => (
  <Box as="div" parent="SelectionInput" name="Container">
    {icon && (
      <Box as="div" parent="SelectionInput" name="Icon">
        <Icon name={icon} color="text-primary" />
      </Box>
    )}
    <Box as="div" parent="SelectionInput" name="Body">
      <Box as="label" parent="SelectionInput" name="Label" htmlFor={id}>
        {labelText && (
          <Box as="span" parent="SelectionInput" name="LabelText">
            {labelText}
          </Box>
        )}
        {counter && (
          <Box as="span" parent="SelectionInput" name="Counter">
            <Text.Regular color="text-secondary">{counter}</Text.Regular>
          </Box>
        )}
      </Box>
      {helperText && (
        <Box as="span" parent="SelectionInput" name="Helper">
          <Text.Regular color="text-secondary">{helperText}</Text.Regular>
        </Box>
      )}
    </Box>
  </Box>
);

const SelectionInput = ({
  value,
  isChecked,
  labelText,
  ariaLabel,
  counter,
  icon,
  helperText,
  onChange,
  componentName,
  CheckMark,
  inputRef,
  disabled,
  id = `${componentName}-${camelcase(labelText)}-${camelcase(value)}`,
  name,
  ...rest
}: TSelectionInput): ReactElement => (
  <Box as="div" name="SelectionInput" modifiers={componentName} {...rest}>
    <Box as="div" parent="SelectionInput" name="InputContainer">
      <Input
        value={value}
        isChecked={isChecked}
        id={id}
        onChange={onChange}
        ariaLabel={ariaLabel}
        componentName={componentName}
        inputRef={inputRef}
        disabled={disabled}
        htmlNameAttr={name}
      />
      {CheckMark && isChecked && <CheckMark />}
    </Box>
    <SelectionInputBody icon={icon} id={id} counter={counter} labelText={labelText} helperText={helperText} />
  </Box>
);

export default SelectionInput;
